import ErrorIcon from '@mui/icons-material/Error';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	Link,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { isAxiosError } from 'axios';
import pino from 'pino';
import { lazy, Suspense, useState } from 'react';

import { notificationStrings, sessionStrings } from '../../../../../constants/strings';
import { UI_BUTTON_PRIMARY, UI_BUTTON_SECONDARY } from '../../../../../constants/uiConstants';
import { userKeys } from '../../../../../queries/query-keys';
import { useLoginQuery, useSessionLogoutQuery } from '../../../../../queries/sessionQuery';
import UserRepository from '../../../../../repositories/userRepository';
import { queryClient } from '../../../../../router/Root';
import { useSigninMessageNavigation } from '../../../../../router/routerUtils';
import { isCompanyOrSupportAdmin, isSupportAdmin } from '../../../../../selectors/userSelectors';
import { useZustand } from '../../../../../zustandStore';
import SimpleButton from '../../../../atoms/button/simpleButton';
import { StyledBox } from '../../common/styledBox';
import { StyledButtonLink } from '../../common/styledButtonLink';
import { StyledText } from '../../common/styledText';
import { useValidation } from '../../hsbc/hooks/useValidation';
import FormInput from '../../hsbc/steps/common/formInput';
import {
	twoFactorSchema,
	type TwoFactorSchemaType,
} from '../../hsbc/steps/defaultRegistration/step_4/setupTwoFactorAuth';
import { useEmailInput } from '../hooks/useEmailInput';
import { usePasswordInput } from '../hooks/usePasswordInput';

const EnterValidationCode = lazy(() => import('./enterValidationCode'));
const SetupTwoFactorAuth = lazy(() => import('../../activateForm/setupTwoFactorAuth'));
const VerifyPhoneSetupForm = lazy(() => import('./verifyPhoneSetupForm'));

import type { ChangeEvent, FC, FocusEvent as ReactFocusEvent, FormEvent, PropsWithChildren } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { AmexLoadingButton } from '../../../../../material-ui-theme/components/styled/AmexLoadingButton';
import { HsbcLoadingButton } from '../../../../../material-ui-theme/components/styled/HsbcsLoadingButton';
import { SessionRepository } from '../../../../../repositories/sessionRepository';
import { getPreLoginRouterState } from '../../../../../router/routerUtils';
import { invalidOtpCodeSentMessage } from '../../activateForm/setupTwoFactorAuth';
import type { MFARegister } from '../../hsbc/steps/defaultRegistration/step_3/passwordSetupForm';

const logger = pino();

const LoadingBtn = globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP ? HsbcLoadingButton : AmexLoadingButton;

const PasswordLogin: FC<PropsWithChildren> = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const { redirectParams, inactivityLogoutMessage = '' } = location.state || {}; // Fallback in case state is undefined
	const inactivityLogoutParam = searchParams.get('inactivityLogout');
	const { email } = redirectParams || {};
	const { state: password, handlePasswordChange, handlePasswordValidation, clearPasswordState } = usePasswordInput();
	const { state: emailState, handleEmailChange, handleEmailValidation } = useEmailInput(email);
	const { validateField, clearFieldError, fieldErrors } = useValidation<TwoFactorSchemaType>();
	const login = useLoginQuery<MFARegister>();
	const { navigateToLockedAccountMessage } = useSigninMessageNavigation();
	const {
		setLoginMethod,
		setNotification,
		setPageLoading,
		setStepsData,
		setIsFormLoading,
		setIsMfaAppSetupRequired,
		setIsMfaSmsSetupRequired,
		setIsPhoneSetupRequired,
		setIsMfaLoginRequired,
		setFormState,
		setShowTwoFactorAuth,
		setMfaData,
		passwordLogin,
		registration,
	} = useZustand(state => ({
		...state,
	}));

	const theme = useTheme();
	const logout = useSessionLogoutQuery();
	const [showPassword, setShowPassword] = useState(false);
	const [loginError, setLoginError] = useState('');

	const onLoginSuccess = async (data: MFARegister | undefined) => {
		try {
			if (globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP) {
				if (data?.passwordAttemptsExceeded) {
					navigateToLockedAccountMessage('password');
					return;
				}

				if (data?.mfaCodeAttemptsExceeded) {
					navigateToLockedAccountMessage('password');
					return;
				}
			}
			const user = await UserRepository.getUser();
			queryClient.setQueryData([userKeys.getUser], user);

			const isTsAdmin = isSupportAdmin(user?.roles);

			if (user && !isTsAdmin && !isCompanyOrSupportAdmin(user?.roles) && user?.roles.length === 0) {
				setNotification([
					{
						type: notificationStrings.ERROR,
						message: 'Your account has been disabled, please contact your company or team administrator.',
						onClose: () => {
							logout.mutate();
						},
					},
				]);
			} else if (isTsAdmin) {
				void navigate('/settings/overview');
			} else {
				const previousRoute = getPreLoginRouterState();
				if (previousRoute.redirectTo && !previousRoute.redirectTo.includes('signin')) {
					void navigate(`${previousRoute.redirectTo}${previousRoute.redirectParams}`);
					return;
				}
				const path = isCompanyOrSupportAdmin(user?.roles || []) ? '/wallet' : '/wallet/my-cards';
				globalThis.location.pathname = path;
			}
		} catch (error) {
			logger.error({ message: 'error in onActivateUser', error });
			setNotification([
				{
					type: notificationStrings.ERROR,
					message: 'An error occurred while activating the user. Please try again.',
				},
			]);
		}
	};

	const handleLoginError = (error: unknown): void => {
		if (!isAxiosError(error)) {
			setNotification([
				{
					type: notificationStrings.WARNING,
					message: 'An error occurred, please try again later.',
				},
			]);
			return;
		}

		const errorStatus = error.response?.status;
		const errorCode = error.response?.data?.errorCode;

		if (globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP) {
			if (errorStatus === 403 && (errorCode === 'LOCKED_ACCOUNT' || !errorCode)) {
				logger.warn({ message: 'Account locked due to multiple failed attempts', errorCode });
				navigateToLockedAccountMessage('password');
				return;
			}

			if (errorStatus === 403 && errorCode === 'INCOMPLETE_ACTIVATION') {
				logger.warn({ message: 'Account activation is incomplete', errorCode });
				navigateToLockedAccountMessage('accountNotCreated');
				return;
			}

			if (errorStatus === 401) {
				setLoginError("You've entered an invalid email or password, please try again");
				return;
			}
		}

		if (errorStatus === 401) {
			setLoginError('Invalid email or password. Please check your credentials.');
			return;
		}

		setNotification([
			{
				type: notificationStrings.WARNING,
				message: 'An error occurred, please try again later.',
			},
		]);
	};

	const handleLoginSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!emailState.value) {
			return;
		}
		setIsFormLoading(true);
		setLoginError(''); // Clear previous error
		login.mutate(
			{ username: String(emailState.value), password: password.passwordValue },
			{
				onSuccess: async data => {
					setLoginError(''); // Clear error on success
					setNotification([]);
					setMfaData(data); // for Amex

					if (globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP) {
						if (data.activationCodeAttemptsExceeded) {
							navigateToLockedAccountMessage('activation');
							return;
						}
						if (data.mfaCodeAttemptsExceeded) {
							navigateToLockedAccountMessage('mfa');
							return;
						}
					}

					if (data?.mfaOtp?.status === 'setup_required') {
						setShowTwoFactorAuth(true);
					}

					if (data?.mfaOtp) {
						setStepsData({
							mfaOtp: {
								status: data.mfaOtp.status,
								secret: data.mfaOtp.secret || '',
								qrCode: data.mfaOtp.qrCode,
							},
						});
					}

					if (data?.mfaSms) {
						setStepsData({
							mfaSms: {
								status: data.mfaSms.status,
								phoneDetails: data.mfaSms.phoneDetails,
							},
						});
					}

					if (data?.phoneSetup) {
						setStepsData({
							phoneSetup: {
								status: data.phoneSetup.status,
								phoneDetails: data.phoneSetup.phoneDetails,
							},
						});
					}

					// the order here matters
					if (data?.mfaOtp?.status === 'setup_required') {
						setIsMfaAppSetupRequired(true);
					} else if (data?.mfaSms?.status === 'login_required') {
						setIsMfaSmsSetupRequired(true);
					} else if (data?.phoneSetup?.status === 'verification_needed') {
						setIsPhoneSetupRequired(true);
					} else if (data?.mfaOtp?.status === 'login_required') {
						setIsMfaLoginRequired(true);
					} else {
						void onLoginSuccess(data);
					}
				},
				onError: (error: unknown) => {
					handleLoginError(error);
				},
				onSettled: () => {
					clearPasswordState();
					setIsFormLoading(false);
				},
			},
		);
	};

	const handleLoginMfaInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormState({ ...passwordLogin.formState, [name]: value });
		clearFieldError(name as keyof TwoFactorSchemaType);
	};

	const handleLoginMfaInputBlur = (e?: ReactFocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (e) {
			const { name = '' } = e.target;
			validateField(twoFactorSchema, name as keyof TwoFactorSchemaType, passwordLogin.formState);
		}
	};

	const handleLoginMfaSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setPageLoading(true);

		try {
			await SessionRepository.sendMfaCodeChallenge({
				code: passwordLogin.formState.authCode,
				challengeName: 'SOFTWARE_TOKEN_MFA',
			});
			return onLoginSuccess(undefined);
		} catch (error) {
			logger.error({ error });
			setNotification([
				{
					type: notificationStrings.ERROR,
					message: invalidOtpCodeSentMessage,
				},
			]);
		} finally {
			setPageLoading(false);
		}
	};

	if (passwordLogin.isMfaAppSetupRequired || registration.showTwoFactorAuth) {
		return (
			<Suspense fallback={<div />} key={location.state?.key}>
				<SetupTwoFactorAuth showNotificationMessage={!globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP} />
			</Suspense>
		);
	} else if (passwordLogin.isMfaSmsSetupRequired) {
		return (
			<Suspense fallback={<div />} key={location.state?.key}>
				<EnterValidationCode challengeName="SMS_MFA" />
			</Suspense>
		);
	} else if (passwordLogin.isPhoneSetupRequired) {
		return (
			<Suspense fallback={<div />} key={location.state?.key}>
				<VerifyPhoneSetupForm />
			</Suspense>
		);
	}

	if (passwordLogin.isMfaLoginRequired) {
		return (
			<form
				data-testid="mfa-login-form"
				style={{
					padding: `0 ${theme.tsGoUnit.tsUnit} ${theme.tsGoUnit.tsUnit}`,
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: theme.tsGoUnit.tsUnit,
				}}
				onSubmit={handleLoginMfaSubmit}
			>
				<FormInput
					label="Authenticator app code"
					customInputLabelProps={{ style: { fontWeight: theme.typography.tsFontWeight.default } }}
					name="authCode"
					value={passwordLogin.formState.authCode}
					error={fieldErrors['authCode']}
					onChange={handleLoginMfaInputChange}
					onBlur={handleLoginMfaInputBlur}
					placeholder="Enter a code from your authenticator app"
				/>
				<LoadingBtn
					data-testid="login-code-button"
					style={{ width: '100%', fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default) }}
					color={UI_BUTTON_PRIMARY}
					disabled={!twoFactorSchema.safeParse(passwordLogin.formState).success}
					type="submit"
					loading={passwordLogin.isFormLoading}
					loadingPosition="start"
					startIcon={<></>}
					variant="contained"
				>
					{'Send code'}
				</LoadingBtn>
			</form>
		);
	}

	return (
		<form
			key={location.state?.key}
			data-testid="form"
			style={{
				padding: `0 ${theme.tsGoUnit.tsUnit} ${theme.tsGoUnit.tsUnit}`,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: theme.tsGoUnit.tsUnit,
			}}
			onSubmit={handleLoginSubmit}
		>
			{globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP && (inactivityLogoutParam || inactivityLogoutMessage !== '') ? (
				<Typography variant="subtitle1" sx={{ mt: 0 }}>
					{inactivityLogoutParam
						? "You've been logged out due to inactivity, please login again"
						: inactivityLogoutMessage}
				</Typography>
			) : null}
			<FormControl style={{ padding: 0 }} fullWidth variant="outlined" error={emailState.isError}>
				<TextField
					label={'Email'}
					style={{ padding: 0, margin: 0 }}
					type="email"
					required
					fullWidth
					margin="normal"
					variant="outlined"
					autoComplete="off"
					value={email || emailState.value}
					error={emailState.isError}
					onChange={e => handleEmailChange(e.target.value)}
					onBlur={handleEmailValidation}
					slotProps={{
						htmlInput: { 'data-testid': 'email' },
						inputLabel: { style: { fontWeight: theme.typography.tsFontWeight.default } },
					}}
				/>
				{emailState.isError && (
					<FormHelperText style={{ marginLeft: 0 }}>
						<ErrorIcon fontSize="small" style={{ verticalAlign: 'middle' }} /> {'Invalid email address'}
					</FormHelperText>
				)}
			</FormControl>
			<FormControl style={{ padding: 0 }} fullWidth variant="outlined" error={password.isPasswordError || !!loginError}>
				<TextField
					name="password"
					label="Password"
					style={{ padding: 0, margin: 0 }}
					type={showPassword ? 'text' : 'password'}
					required
					fullWidth
					margin="normal"
					variant="outlined"
					autoComplete="current-password"
					onChange={handlePasswordChange}
					onBlur={handlePasswordValidation}
					value={password.passwordValue}
					error={password.isPasswordError || !!loginError}
					slotProps={{
						htmlInput: {
							'data-testid': 'password',
							autoComplete: 'current-password',
						},
						inputLabel: { style: { fontWeight: theme.typography.tsFontWeight.default } },
						input: {
							endAdornment: (
								<InputAdornment position="end">
									<IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
										{showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
									</IconButton>
								</InputAdornment>
							),
						},
					}}
				/>
				{loginError && (
					<FormHelperText
						error
						style={{
							marginLeft: 0,
							color: theme.palette.error.main,
							display: 'flex',
							alignItems: 'flex-start',
							gap: '4px',
						}}
					>
						<ErrorIcon fontSize="small" style={{ marginTop: '2px' }} />
						<span>{loginError}</span>
					</FormHelperText>
				)}
			</FormControl>
			<Grid
				container
				sx={{
					justifyContent: 'flex-end',
					width: '100%',
				}}
			>
				<StyledButtonLink
					data-testid="forgot-password-button"
					onClick={() => {
						void navigate('/signin/password/forgot');
					}}
				>
					{'Forgot your password?'}
				</StyledButtonLink>
			</Grid>
			<LoadingBtn
				data-testid="login-button"
				color={UI_BUTTON_PRIMARY}
				type="submit"
				loading={passwordLogin.isFormLoading}
				loadingPosition="start"
				startIcon={<></>}
				variant="contained"
			>
				{sessionStrings.CONTINUE}
			</LoadingBtn>
			{globalThis.cardProvider.IS_AMEX_PROVIDER_SETUP ? (
				<>
					<StyledBox>
						<StyledText variant="body1">{'Or use Single Sign-On (SSO)'}</StyledText>
					</StyledBox>
					<SimpleButton
						dataTestId="conmtinueWithSso-btn"
						style={{
							width: '100%',
							border: `1px solid ${theme.palette.tsGoColor.pink}`,
							color: theme.palette.tsGoColor.pink,
							fontSize: theme.typography.pxToRem(theme.typography.tsFontSize.default),
						}}
						color={UI_BUTTON_SECONDARY}
						label={'Continue with SSO'}
						onClick={() => setLoginMethod({ isSSOLogin: true })}
					/>
					<Typography>
						{'Need Help? Contact us at: '}
						<Link underline="none" href="mailto:go@tradeshift.com" target="_blank">
							Go@Tradeshift.com
						</Link>
					</Typography>
				</>
			) : null}
		</form>
	);
};

export default PasswordLogin;
